import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, AuthContextType } from '../types';
import { apiGet, apiPost } from '../lib/data';
import { isNetworkError, retryWithBackoff } from './DataContext';

const AuthContext = createContext<AuthContextType | null>(null);

const NETWORK_MESSAGE =
  'مشكلة في الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت والمحاولة مرة أخرى.';
const OFFLINE_MESSAGE =
  'لا يوجد اتصال بالإنترنت. يرجى التحقق من اتصالك والمحاولة مرة أخرى.';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [transitionMessage, setTransitionMessage] = useState<string | null>(null);

  useEffect(() => {
    if (user && transitionMessage) {
      setTransitionMessage(null);
    }
  }, [user, transitionMessage]);

  /**
   * Restores the signed-in user on load.
   *
   * This used to be three steps -- read the stored session, wait 300 ms, then
   * fetch the profile -- because the session and the profile came from
   * different places. One request returns both, so the wait and the second
   * round trip are gone.
   */
  useEffect(() => {
    let cancelled = false;

    apiGet<{ user: User | null }>('/api/auth/session')
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error('خطأ في جلب بيانات المستخدم:', error);
        setUser(data?.user ?? null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const login = async (email: string, password: string) => {
    setTransitionMessage('جاري تسجيل الدخول الى المنصة...');
    try {
      if (!navigator.onLine) throw new Error(OFFLINE_MESSAGE);

      const signedIn = await retryWithBackoff(async () => {
        const { data, error } = await apiPost<{ user: User }>('/api/auth/sign-in', { email, password });
        if (error) throw error;
        return data;
      });

      setUser(signedIn?.user ?? null);
      setIsLoading(false);
    } catch (error) {
      setTransitionMessage(null);
      console.error('خطأ في تسجيل الدخول:', error);
      throw new Error(
        isNetworkError(error)
          ? NETWORK_MESSAGE
          : (error as { message?: string })?.message || 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
      );
    }
  };

  const register = async (
    full_name: string,
    email: string,
    password: string,
    authority?: string,
    region?: string,
    cluster?: string,
    facility?: string,
    recaptchaToken?: string,
  ) => {
    try {
      if (!navigator.onLine) throw new Error(OFFLINE_MESSAGE);

      // Two authorities describe a kind of access rather than a place of work,
      // so they carry no region, cluster or facility.
      const isReferralCenter = authority === 'مركز الإحالات';
      const isViewer = authority === 'عرض التخصصات';
      const isSpecialAuthority = isReferralCenter || isViewer;

      const role = isReferralCenter ? 'rc_view' : isViewer ? 'viewer' : 'user';

      await retryWithBackoff(async () => {
        const { error } = await apiPost('/api/auth/sign-up', {
          full_name,
          email,
          password,
          role,
          authority,
          region: isSpecialAuthority ? null : region,
          cluster: isSpecialAuthority ? null : cluster,
          facility: isSpecialAuthority ? null : facility,
          recaptchaToken,
        });
        if (error) throw error;
      });

      // A new account waits for an administrator to activate it, so signing up
      // does not sign anyone in.
      setUser(null);
    } catch (error) {
      console.error('خطأ في التسجيل:', error);
      throw new Error(
        isNetworkError(error)
          ? NETWORK_MESSAGE
          : (error as { message?: string })?.message || 'حدث خطأ أثناء إنشاء الحساب',
      );
    }
  };

  const logout = async () => {
    setTransitionMessage('جاري تسجيل الخروج...');
    const minDisplay = new Promise(resolve => setTimeout(resolve, 700));
    let thrownError: Error | null = null;

    try {
      const { error } = await apiPost('/api/auth/sign-out');
      setUser(null);
      if (error && !isNetworkError(error)) throw error;
    } catch (error) {
      console.error('خطأ في تسجيل الخروج:', error);
      setUser(null);
      if (isNetworkError(error)) {
        console.warn('Network error during logout, but local session cleared');
      } else {
        thrownError = new Error('حدث خطأ أثناء تسجيل الخروج');
      }
    }

    await minDisplay;
    setTransitionMessage(null);
    if (thrownError) throw thrownError;
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, isLoading, transitionMessage }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
